import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchData } from '../../helpers/apiUtils';
import { ROLLOUT_TEXTE, RolloutVorlaufBefund, RolloutZustand } from './types';

interface Props {
  brokerId: number | null;
  brokerName: string;
  onSchliessen: () => void;
  /** Wird nach einem Rollout gerufen, damit die Matrix ihren Zustand neu lädt. */
  onFertig: () => void;
}

/**
 * „Broker auf allen Portalen neu ausspielen" — mit Vorlauf, Auswahl und Zustand.
 *
 * ============================================================================================
 * WARUM DIESER DIALOG SO AUSSIEHT
 * ============================================================================================
 * Christian ändert den Risikohinweis EINMAL und erwartet ihn auf allen sechs Portalen. Der
 * gefährliche Ausgang ist nicht der Fehlschlag — es ist der HALBE Erfolg: drei Portale zeigen
 * 81 %, drei stehen bei 80 %, der Knopf war grün, die Meldung ist weggeklickt. Deshalb drei
 * Dinge, die ein einfacher Knopf nicht leistet:
 *
 *   1. VORHER sagen, was scheitern würde — und dann gar nicht erst anfangen. Ein Rollout, der
 *      nach dem dritten Portal abbricht, ist schlimmer als einer, der nie beginnt.
 *   2. Den Zustand NACH dem Klick weiter anzeigen. Er steht in der Datenbank, nicht in dieser
 *      Sitzung: wer den Dialog morgen öffnet, sieht dieselbe Unvollständigkeit.
 *   3. Gezielt nur die fehlenden Portale wiederholen.
 *
 * ⚠ Die Tabelle ist eine ANZEIGE, keine Zusage. Massgeblich ist, was der Server beim Ausführen
 * auflöst; was tatsächlich passiert ist, steht in seiner Antwort — und die lädt dieser Dialog
 * danach neu, statt die Tabelle optimistisch fortzuschreiben.
 */
const BrokerRolloutDialog: React.FC<Props> = ({ brokerId, brokerName, onSchliessen, onFertig }) => {
  const [zustand, setZustand] = useState<RolloutZustand | null>(null);
  const [befunde, setBefunde] = useState<RolloutVorlaufBefund[] | null>(null);
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<Array<{ siteKey: string; ok: boolean; error?: string }> | null>(null);

  const laden = useCallback(async () => {
    if (brokerId === null) return;
    setZustand(null);
    setBefunde(null);
    setFehler(null);
    setErgebnis(null);
    setAbgewaehlt(new Set());
    const [z, v] = await Promise.all([
      fetchData<RolloutZustand>(`/api/broker-hub/brokers/${brokerId}/rollout`),
      fetchData<{ befunde: RolloutVorlaufBefund[]; bereit: boolean }>(
        `/api/broker-hub/brokers/${brokerId}/rollout/vorlauf`,
      ),
    ]);
    if (z.success && z.data) setZustand(z.data);
    else setFehler(z.error || 'Der Rollout-Zustand liess sich nicht laden.');
    if (v.success && v.data) setBefunde(v.data.befunde);
  }, [brokerId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const fuehrt = (zustand?.portale ?? []).filter((p) => p.zustand !== 'kein_profil');
  const offen = fuehrt.filter((p) => p.zustand !== 'aktuell');
  const gewaehlt = fuehrt.filter((p) => !abgewaehlt.has(p.siteKey));
  const grundFuer = (siteKey: string) => befunde?.find((b) => b.siteKey === siteKey)?.grund ?? null;

  const ausspielen = async (nur?: string[]) => {
    if (brokerId === null) return;
    setLaeuft(true);
    setFehler(null);
    const r = await fetchData<{
      zustand: RolloutZustand;
      ergebnisse: Array<{ siteKey: string; ok: boolean; error?: string }>;
    }>(`/api/broker-hub/brokers/${brokerId}/rollout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nur?.length ? { sites: nur } : {}),
    });
    setLaeuft(false);
    if (r.success && r.data) {
      setZustand(r.data.zustand);
      setErgebnis(r.data.ergebnisse);
      onFertig();
      // Der Vorlauf kann sich durch den Lauf geändert haben — neu holen, nicht fortschreiben.
      const v = await fetchData<{ befunde: RolloutVorlaufBefund[] }>(
        `/api/broker-hub/brokers/${brokerId}/rollout/vorlauf`,
      );
      if (v.success && v.data) setBefunde(v.data.befunde);
    } else {
      // ⚠ Der 409-Fall ist KEIN Ausfall: der Vorlauf hat den Rollout gar nicht erst gestartet.
      // Die Befunde aus der Antwort sind genau die Liste, die der Autor abarbeiten muss.
      const b = (r as unknown as { befunde?: RolloutVorlaufBefund[] }).befunde;
      if (Array.isArray(b)) setBefunde(b);
      setFehler(r.error || 'Der Rollout ist fehlgeschlagen.');
    }
  };

  return (
    <Dialog open={brokerId !== null} onClose={onSchliessen} maxWidth="md" fullWidth>
      <DialogTitle>Auf allen Portalen ausspielen: {brokerName}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Risikohinweis und Affiliate-Link kommen aus <strong>einer</strong> Quelle: der Hinweis
          aus der Compliance-Verwaltung, der Link aus dem jeweiligen Portal-Profil. Dieser Knopf
          bringt beide auf die Portale.
        </Alert>

        {fehler && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fehler}
          </Alert>
        )}

        {befunde !== null && befunde.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>So wird nicht angefangen.</strong> {befunde.length} Portal(e) würden scheitern
            — der Hub spielt dann gar nichts aus, damit kein halb fertiger Stand entsteht:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {befunde.map((b) => (
                <li key={b.siteKey}>
                  <strong>{b.siteKey}</strong>: {b.grund}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {zustand === null && !fehler && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        )}

        {zustand && (
          <>
            <Alert severity={zustand.vollstaendig ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {zustand.vollstaendig
                ? 'Alle Portale stehen auf dem aktuellen Stand.'
                : `Unvollständig — ${offen.length} von ${fuehrt.length} Portalen sind nicht auf dem aktuellen Stand.`}
            </Alert>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Portal</TableCell>
                  <TableCell>Zustand</TableCell>
                  <TableCell>Zuletzt ausgespielt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zustand.portale.map((p) => {
                  const t = ROLLOUT_TEXTE[p.zustand];
                  const fuehrtBroker = p.zustand !== 'kein_profil';
                  const grund = grundFuer(p.siteKey);
                  const lauf = ergebnis?.find((e) => e.siteKey === p.siteKey);
                  return (
                    <TableRow key={p.siteKey} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          disabled={!fuehrtBroker}
                          checked={fuehrtBroker && !abgewaehlt.has(p.siteKey)}
                          onChange={() =>
                            setAbgewaehlt((v) => {
                              const n = new Set(v);
                              if (n.has(p.siteKey)) n.delete(p.siteKey);
                              else n.add(p.siteKey);
                              return n;
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>{p.siteKey}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t.text}
                          color={t.ton === 'default' ? undefined : t.ton}
                          variant={t.ton === 'default' ? 'outlined' : 'filled'}
                        />
                        {grund && (
                          <Typography variant="caption" display="block" color="error">
                            {grund}
                          </Typography>
                        )}
                        {lauf && !lauf.ok && (
                          <Typography variant="caption" display="block" color="error">
                            Letzter Lauf: {lauf.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{p.ausgespieltAm ?? '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={laeuft}>
          Schliessen
        </Button>
        {/*
          ⚠ Die Wiederholung schickt NUR die offenen Portale mit. Ohne diese Einschränkung liefe
          der Vorlauf auch über die bereits fertigen — und ein Portal, das dauerhaft klemmt,
          blockierte damit für immer die Wiederholung der übrigen.
        */}
        {zustand && !zustand.vollstaendig && offen.length > 0 && (
          <Button
            onClick={() => ausspielen(offen.map((p) => p.siteKey))}
            disabled={laeuft}
            variant="outlined"
          >
            Nur die {offen.length} fehlenden erneut versuchen
          </Button>
        )}
        <Button
          onClick={() => ausspielen(gewaehlt.length === fuehrt.length ? undefined : gewaehlt.map((p) => p.siteKey))}
          disabled={laeuft || zustand === null || gewaehlt.length === 0}
          variant="contained"
        >
          {laeuft ? 'Läuft…' : `Auf ${gewaehlt.length} Portal(en) ausspielen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerRolloutDialog;
